// `GET /rounds/leaderboard` — the cross-player score board for one seed (`home-and-single-player`).
//
// **Types first**, like `rounds-tournament.ts`: the outcome vocabulary is what the Leaderboard screen
// renders against, and the two facts the route exists to state are the two this module refuses to
// derive.
//
//   * **The order is the server's.** Entries arrive highest score first and are passed through
//     untouched. A client-side re-sort would agree with the server on every correct day and hide the
//     one day it did not.
//   * **The scope is the server's too.** `global` where authentication is enabled, `self` where it is
//     not — because with the gate off every run collapses onto one sentinel player, so the board is
//     genuinely one row of that player's own bests. It is *relabelled*, never hidden: a feature only
//     exercised on the hosted deployment is a feature nobody develops against.
//
// The route takes **no query parameters** and presents **no host bearer**: it is gated by whatever
// gates the rest of the API, which is the injected `fetch`'s business and not this module's. There is
// deliberately no `Authorization` header written here.

/** One player's best score on this seed and run-config digest — one row per player. */
export interface LeaderboardEntry {
  /** The player's stable identifier. Opaque: it is never parsed and never displayed. */
  player: string;
  /** Never null on the wire; a row that breaks that is dropped rather than drawn blank. */
  displayName: string;
  score: number;
  /** The agent-chosen name for the script that scored it, or `null` — in which case the display
   * falls back to `#{documentOrdinal}`, the stable never-reused ordinal. */
  scriptName: string | null;
  documentOrdinal: number;
  /** The prompt behind that script, revealed when the entry is opened. `null` for a script no
   * prompt produced. */
  prompt: string | null;
}

export interface Leaderboard {
  /** The seed every score on this board was achieved on. A new event on a new seed starts a second
   * ladder rather than corrupting this one.
   *
   * **A string, not a number, and deliberately so.** The seed is a `u64` server-side and JSON
   * numbers are doubles, so one above 2^53 would arrive silently rounded — and a rounded seed names
   * a different world. It is only ever displayed here, never parsed and never compared, so a string
   * costs nothing and cannot be corrupted in transit. */
  seed: string;
  /** `global` — everyone's bests. `self` — this deployment has authentication off, so every run is
   * one sentinel player's and the board is that player's own bests. */
  scope: 'global' | 'self';
  /** Highest score first, **as the server ordered them**. */
  entries: readonly LeaderboardEntry[];
}

export type LeaderboardOutcome =
  | { kind: 'read'; board: Leaderboard }
  /** The request never got an answer, was refused, or the answer was not the JSON it claimed.
   * Deliberately **not** collapsed to an empty board: "nobody has scored yet" is a real state the
   * screen says differently. */
  | { kind: 'unavailable'; message: string };

export interface LeaderboardConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

function entryFrom(row: unknown): LeaderboardEntry | null {
  if (row === null || typeof row !== 'object') {
    return null;
  }
  const entry = row as Record<string, unknown>;
  if (
    typeof entry['player'] !== 'string' ||
    typeof entry['displayName'] !== 'string' ||
    entry['displayName'] === '' ||
    typeof entry['score'] !== 'number' ||
    typeof entry['documentOrdinal'] !== 'number'
  ) {
    return null;
  }
  return {
    player: entry['player'],
    displayName: entry['displayName'],
    score: entry['score'],
    scriptName: typeof entry['scriptName'] === 'string' ? entry['scriptName'] : null,
    documentOrdinal: entry['documentOrdinal'],
    prompt: typeof entry['prompt'] === 'string' ? entry['prompt'] : null,
  };
}

/** One read of the board. Never throws and never rejects: a screen is not something a mount may die
 * on, and every failure reads as {@link LeaderboardOutcome} `unavailable`. */
export async function fetchLeaderboard(config: LeaderboardConfig = {}): Promise<LeaderboardOutcome> {
  const baseUrl = (config.baseUrl ?? '').replace(/\/+$/, '');
  const fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);

  let response: Response;
  try {
    response = await fetchImpl(`${baseUrl}/rounds/leaderboard`, { method: 'GET' });
  } catch {
    return { kind: 'unavailable', message: 'Can’t reach the hive.' };
  }
  if (!response.ok) {
    return { kind: 'unavailable', message: `The hive refused the leaderboard (${response.status}).` };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'unavailable', message: 'The hive answered with something unreadable.' };
  }
  if (body === null || typeof body !== 'object') {
    return { kind: 'unavailable', message: 'The hive answered with something unreadable.' };
  }
  const row = body as Record<string, unknown>;
  if (typeof row['seed'] !== 'string' || !Array.isArray(row['entries'])) {
    return { kind: 'unavailable', message: 'The hive answered with something unreadable.' };
  }
  const entries: LeaderboardEntry[] = [];
  for (const candidate of row['entries']) {
    const entry = entryFrom(candidate);
    if (entry !== null) {
      entries.push(entry);
    }
  }
  return {
    kind: 'read',
    board: {
      seed: row['seed'],
      // `global` is not a default: an unrecognised scope is read as `self`, which is the *narrower*
      // claim — labelling a one-player board as everyone's would be the misleading direction.
      scope: row['scope'] === 'global' ? 'global' : 'self',
      entries,
    },
  };
}
